import { Area } from 'react-easy-crop';

export const createImage = (url: string): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener('load', () => resolve(image));
    image.addEventListener('error', (error) => reject(error));
    image.setAttribute('crossOrigin', 'anonymous'); // needed to avoid cross-origin issues on CodeSandbox
    image.src = `${url}?query=${Date.now()}`;
  });

export function getRadianAngle(degreeValue: number) {
  return (degreeValue * Math.PI) / 180;
}

/**
 * Returns the new bounding area of a rotated rectangle.
 */
export function rotateSize(width: number, height: number, rotation: number) {
  const rotRad = getRadianAngle(rotation);

  return {
    width: Math.abs(Math.cos(rotRad) * width) + Math.abs(Math.sin(rotRad) * height),
    height: Math.abs(Math.sin(rotRad) * width) + Math.abs(Math.cos(rotRad) * height),
  };
}

export async function getCroppedImageFile(
  imageSrc: string,
  crop: Area,
  rotation = 0,
  mime = 'image/png'     // keep PNG by default, or pass 'image/jpeg'
): Promise<File | null> {
  const image = await new Promise<HTMLImageElement>((res, rej) => {
    const img = new Image();
    img.crossOrigin = 'anonymous'; // keeps CORS happy for remote sources
    img.onload = () => res(img);
    img.onerror = rej;
    img.src = `${imageSrc}?t=${Date.now()}`;
  });

  const dpr = window.devicePixelRatio || 1;
  const canvas = document.createElement('canvas');
  canvas.width = crop.width * dpr;
  canvas.height = crop.height * dpr;

  const ctx = canvas.getContext('2d');
  if (!ctx) return null;

  // prevent browser from blurring while scaling the image up to DPR size
  ctx.imageSmoothingEnabled = false;

  ctx.save();
  ctx.scale(dpr, dpr); // draw in CSS pixels, scaled up
  if (rotation) {
    // move origin to centre, rotate, move back
    ctx.translate(crop.width / 2, crop.height / 2);
    ctx.rotate((rotation * Math.PI) / 180);
    ctx.translate(-crop.width / 2, -crop.height / 2);
  }
  ctx.drawImage(
    image,
    crop.x, // top‑left of source rectangle (original pixels)
    crop.y,
    crop.width,
    crop.height,
    0, 0, // draw to (0,0) of the canvas
    crop.width,
    crop.height
  );
  ctx.restore();

  return new Promise<File | null>((resolve) =>
    canvas.toBlob(
      (blob) =>
        resolve(
          blob
            ? new File([blob], `crop.${mime === 'image/png' ? 'png' : 'jpg'}`, { type: mime })
            : null
        ),
      mime,
      mime.startsWith('image/jpeg') ? 1 : undefined // <- quality = 1 for JPEG
    )
  );
}